import Phaser from 'phaser';
import type { Player } from '../../game/types.js';
import { Button } from './Button.js';
import { COLORS, FONT_HEADER } from '../style.js';
import { DESIGN_HEIGHT, DESIGN_WIDTH } from '../PhaserGame.js';

// Backdrop covers the whole canvas at moderate opacity so the map is
// still visible behind the panel — feels like a result overlay rather
// than a context switch.
const BACKDROP_ALPHA = 0.78;
const BACKDROP_DEPTH = 1000;

const PANEL_WIDTH = 600;
// 800 (vs old 720) leaves clean room for a 4-row podium when a 2v2
// team win means up to 4 entries (2 joint winners + 2 losers).
const PANEL_HEIGHT = 800;
const PANEL_RADIUS = 12;
const PANEL_DEPTH = 1001;

const TITLE_Y_OFFSET = -PANEL_HEIGHT / 2 + 90;
const SUBTITLE_Y_OFFSET = -PANEL_HEIGHT / 2 + 150;
const PODIUM_FIRST_Y_OFFSET = -60;
const PODIUM_ROW_GAP = 96;
const PODIUM_ROW_WIDTH = 480;
const PODIUM_ROW_HEIGHT = 80;
const PODIUM_ROW_RADIUS = 6;
const COLOR_CHIP_SIZE = 36;
const COLOR_CHIP_OFFSET = -PODIUM_ROW_WIDTH / 2 + 80;
const RANK_OFFSET = -PODIUM_ROW_WIDTH / 2 + 32;
const NAME_OFFSET = COLOR_CHIP_OFFSET + 36;

const BUTTON_GAP = 24;
const BUTTON_WIDTH = 220;
const BUTTON_HEIGHT = 64;
const BUTTONS_Y_OFFSET = PANEL_HEIGHT / 2 - 64;

export interface PodiumEntry {
  // Up to 4 because a 2v2 win has 2 joint rank-1 entries + 2 losers
  // at ranks 2 and 3 — and a 4-seat FFA ends with ranks 1..4.
  readonly rank: 1 | 2 | 3 | 4;
  readonly player: Player;
  // True iff this row represents a seat the local user controls.
  // In single-player hot-seat that's any human; in multiplayer it's
  // only this browser's seat. Drives the "(YOU)" tag on the row.
  readonly isLocal: boolean;
}

export interface GameOverModalOptions {
  // True iff the local user (this browser's seat) won. Drives the
  // VICTORY vs DEFEAT headline + panel border color, and the "YOU WIN"
  // vs "{NAME} WINS" subtitle. In single-player hot-seat this is true
  // whenever any human is on the winning team.
  readonly localWon: boolean;
  // True when 2+ entries share rank 1 (team victory). Drives the
  // "TEAM VICTORY / TEAM DEFEAT" subtitle copy.
  readonly teamWin: boolean;
  readonly winner: Player;
  readonly podium: readonly PodiumEntry[]; // ordered 1st → last
  readonly onPlayAgain: () => void;
  readonly onHome: () => void;
}

/**
 * Final-screen overlay shown once a winner is decided. Fixed-size panel
 * centered on the canvas, with a podium of 1st/2nd/3rd and two action
 * buttons (Play Again / Home).
 *
 * The component owns its own backdrop (which also blocks pointer events
 * to anything behind it) and tears down on either button via
 * `destroy()` — the scene calls the user's onPlayAgain / onHome AFTER
 * destroying so the next scene starts cleanly.
 */
export class GameOverModal {
  private readonly objects: Phaser.GameObjects.GameObject[] = [];
  private readonly buttons: Button[] = [];
  private destroyed = false;

  constructor(scene: Phaser.Scene, opts: GameOverModalOptions) {
    const cx = DESIGN_WIDTH / 2;
    const cy = DESIGN_HEIGHT / 2;

    // Full-canvas backdrop. setInteractive() with an explicit hit area
    // swallows taps so the map underneath stays inert while the modal
    // is up.
    const backdrop = scene.add.rectangle(
      cx,
      cy,
      DESIGN_WIDTH,
      DESIGN_HEIGHT,
      0x000000,
      BACKDROP_ALPHA
    );
    backdrop.setDepth(BACKDROP_DEPTH);
    backdrop.setInteractive();
    this.objects.push(backdrop);

    // Panel body — drawn into a Graphics so corners can be rounded.
    const panelBg = scene.add.graphics();
    panelBg.fillStyle(COLORS.bgPanelInt, 1);
    panelBg.fillRoundedRect(
      cx - PANEL_WIDTH / 2,
      cy - PANEL_HEIGHT / 2,
      PANEL_WIDTH,
      PANEL_HEIGHT,
      PANEL_RADIUS
    );
    panelBg.lineStyle(
      2,
      opts.localWon ? COLORS.accentCyanInt : COLORS.accentRedInt,
      1
    );
    panelBg.strokeRoundedRect(
      cx - PANEL_WIDTH / 2,
      cy - PANEL_HEIGHT / 2,
      PANEL_WIDTH,
      PANEL_HEIGHT,
      PANEL_RADIUS
    );
    panelBg.setDepth(PANEL_DEPTH);
    this.objects.push(panelBg);

    // Headline — VICTORY in cyan if the local user won, DEFEAT in red
    // otherwise. Color carries the emotional weight; the subtitle
    // names the actual winner.
    const titleText = opts.localWon ? 'VICTORY' : 'DEFEAT';
    const titleColor = opts.localWon
      ? COLORS.accentCyanHex
      : COLORS.accentRedHex;
    const title = scene.add
      .text(cx, cy + TITLE_Y_OFFSET, titleText, {
        fontFamily: FONT_HEADER,
        fontSize: '52px',
        color: titleColor,
        fontStyle: '700',
      })
      .setLetterSpacing(8)
      .setOrigin(0.5)
      .setDepth(PANEL_DEPTH);
    this.objects.push(title);

    // Subtitle copy depends on who won:
    //   Team win + local on winning team → "TEAM VICTORY"
    //   Team win + local eliminated      → "TEAM DEFEAT"
    //   Solo win + local won             → "YOU WIN"
    //   Solo win + local lost            → "{NAME} WINS"
    const subtitleText = opts.teamWin
      ? opts.localWon ? 'TEAM VICTORY' : 'TEAM DEFEAT'
      : opts.localWon
        ? 'YOU WIN'
        : `${opts.winner.name.toUpperCase()} WINS`;
    const subtitle = scene.add
      .text(cx, cy + SUBTITLE_Y_OFFSET, subtitleText, {
        fontFamily: FONT_HEADER,
        fontSize: '16px',
        color: COLORS.textSecondary,
        fontStyle: '600',
      })
      .setLetterSpacing(4)
      .setOrigin(0.5)
      .setDepth(PANEL_DEPTH);
    this.objects.push(subtitle);

    // Podium — three rows, top-to-bottom = 1st/2nd/3rd. Order in opts
    // is already 1st first, so index 0 sits at the topmost row.
    for (let i = 0; i < opts.podium.length; i++) {
      const entry = opts.podium[i]!;
      const rowY = cy + PODIUM_FIRST_Y_OFFSET + i * PODIUM_ROW_GAP;
      this.drawPodiumRow(scene, cx, rowY, entry);
    }

    // Action buttons — primary "Play Again" left of center, "Home" right.
    const buttonsY = cy + BUTTONS_Y_OFFSET;
    const playAgainBtn = new Button(scene, {
      x: cx - (BUTTON_WIDTH / 2 + BUTTON_GAP / 2),
      y: buttonsY,
      width: BUTTON_WIDTH,
      height: BUTTON_HEIGHT,
      text: 'Play Again',
      style: 'primary',
      onTap: () => {
        this.destroy();
        opts.onPlayAgain();
      },
    });
    const homeBtn = new Button(scene, {
      x: cx + (BUTTON_WIDTH / 2 + BUTTON_GAP / 2),
      y: buttonsY,
      width: BUTTON_WIDTH,
      height: BUTTON_HEIGHT,
      text: 'Home',
      onTap: () => {
        this.destroy();
        opts.onHome();
      },
    });
    // Lift the buttons above the panel + backdrop. Without this they'd
    // render at default depth 0, behind the modal at PANEL_DEPTH.
    playAgainBtn.setDepth(PANEL_DEPTH);
    homeBtn.setDepth(PANEL_DEPTH);
    this.buttons.push(playAgainBtn, homeBtn);
  }

  /**
   * Draws a single podium row: rank number, color chip, team name with
   * "(YOU)" tag if it's the human seat. Uses a Graphics for the row
   * background so corners can be rounded; ranks 1st/2nd/3rd get a hex
   * accent color so the eye lands on the winner.
   */
  private drawPodiumRow(
    scene: Phaser.Scene,
    cx: number,
    cy: number,
    entry: PodiumEntry
  ): void {
    const rowBg = scene.add.graphics();
    rowBg.fillStyle(COLORS.bgElevatedInt, 1);
    rowBg.fillRoundedRect(
      cx - PODIUM_ROW_WIDTH / 2,
      cy - PODIUM_ROW_HEIGHT / 2,
      PODIUM_ROW_WIDTH,
      PODIUM_ROW_HEIGHT,
      PODIUM_ROW_RADIUS
    );
    rowBg.lineStyle(1, COLORS.borderSubtleInt, 1);
    rowBg.strokeRoundedRect(
      cx - PODIUM_ROW_WIDTH / 2,
      cy - PODIUM_ROW_HEIGHT / 2,
      PODIUM_ROW_WIDTH,
      PODIUM_ROW_HEIGHT,
      PODIUM_ROW_RADIUS
    );
    rowBg.setDepth(PANEL_DEPTH);
    this.objects.push(rowBg);

    const rankColors: Record<1 | 2 | 3 | 4, string> = {
      1: COLORS.accentAmberHex, // gold-ish
      2: COLORS.textSecondary,
      3: COLORS.textMuted,
      4: COLORS.textMuted,
    };
    const rankText = scene.add
      .text(cx + RANK_OFFSET, cy, `${entry.rank}`, {
        fontFamily: FONT_HEADER,
        fontSize: '38px',
        color: rankColors[entry.rank],
        fontStyle: '700',
      })
      .setOrigin(0.5)
      .setDepth(PANEL_DEPTH);
    this.objects.push(rankText);

    const colorInt = Phaser.Display.Color.HexStringToColor(
      entry.player.color
    ).color;
    const chip = scene.add.rectangle(
      cx + COLOR_CHIP_OFFSET,
      cy,
      COLOR_CHIP_SIZE,
      COLOR_CHIP_SIZE,
      colorInt
    );
    chip.setStrokeStyle(1, COLORS.borderBrightInt);
    chip.setDepth(PANEL_DEPTH);
    this.objects.push(chip);

    const label = entry.isLocal
      ? `${entry.player.name.toUpperCase()}  (YOU)`
      : entry.player.name.toUpperCase();
    const nameText = scene.add
      .text(cx + NAME_OFFSET, cy, label, {
        fontFamily: FONT_HEADER,
        fontSize: '20px',
        color: COLORS.textPrimary,
        fontStyle: '700',
      })
      .setLetterSpacing(3)
      .setOrigin(0, 0.5)
      .setDepth(PANEL_DEPTH);
    this.objects.push(nameText);
  }

  destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;
    for (const obj of this.objects) obj.destroy();
    this.objects.length = 0;
    for (const btn of this.buttons) btn.destroy();
    this.buttons.length = 0;
  }
}
